import { useEffect, useMemo, useState } from 'react';
import { FiMail, FiRefreshCw, FiSave, FiSearch } from 'react-icons/fi';
import { notify } from '../../../../../_CORE/APP/Templates/_UI/Alerts/AppAlertHost';
import { FieldSelectSearch } from '../../../../../_CORE/APP/Templates/_UI/Form/_index';
import { AdminViewLayout } from '../../_Shared/_UI/AdminViewLayout';
import { usePortfolioAdmin } from '../../_Shared/_Hooks/usePortfolioAdmin';
import { QUOTES_ADMIN_API as API } from '../_MODULES/_API';
import '../../_Shared/_UI/_Styles/PortfolioAdmin.css';
import '../_Styles/Quotes.css';

const STATUS_OPTIONS = [
    ['new', 'Nueva'],
    ['contacted', 'Contactado'],
    ['quoted', 'Presupuestado'],
    ['approved', 'Aprobado'],
    ['rejected', 'Rechazado'],
    ['closed', 'Cerrado'],
];

const STATUS_LABELS = Object.fromEntries(STATUS_OPTIONS);
const STATUS_SELECT_OPTIONS = STATUS_OPTIONS.map(([value, label]) => ({ value, label }));
const STATUS_FILTER_OPTIONS = [{ value: '', label: 'Todos los estados' }, ...STATUS_SELECT_OPTIONS];
const PER_PAGE = 8;

const dateLabel = (value) => {
    const date = value ? new Date(String(value).replace(' ', 'T')) : null;
    if (!date || Number.isNaN(date.getTime())) return value || '-';
    return new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' }).format(date);
};

export const QuotesViewMain = () => {
    const api = usePortfolioAdmin();
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(1);
    const [drafts, setDrafts] = useState({});

    const load = async () => {
        try {
            const data = await api.get(API.LIST);
            const rows = Array.isArray(data) ? data : [];
            setItems(rows);
            setDrafts(Object.fromEntries(rows.map((item) => [
                item.id,
                { status: item.status || 'new', admin_note: item.admin_note || '' },
            ])));
        } catch (error) {
            notify.error('No se pudieron cargar las cotizaciones', error?.message || 'Reintenta en unos segundos.');
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return items.filter((item) => {
            if (status && item.status !== status) return false;
            if (!term) return true;
            return [
                item.quote_code,
                item.customer_name,
                item.customer_contact,
                item.project_type_label,
                item.project_stage_label,
                item.timing_label,
                item.details,
            ].some((value) => String(value || '').toLowerCase().includes(term));
        });
    }, [items, search, status]);

    const counters = useMemo(() => ({
        total: items.length,
        new: items.filter((item) => item.status === 'new').length,
        quoted: items.filter((item) => item.status === 'quoted').length,
        approved: items.filter((item) => item.status === 'approved').length,
    }), [items]);

    useEffect(() => setPage(1), [search, status]);
    const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    const safePage = Math.min(page, totalPages);
    const visible = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

    const updateDraft = (id, field, value) => setDrafts((current) => ({
        ...current,
        [id]: { ...(current[id] || {}), [field]: value },
    }));

    const save = async (item) => {
        const draft = drafts[item.id] || { status: item.status, admin_note: item.admin_note || '' };
        try {
            await api.securePost(API.STATUS_UPDATE, { id: item.id, ...draft }, {}, {
                successTitle: 'Cotización actualizada',
                successText: `${item.quote_code} ahora figura como ${STATUS_LABELS[draft.status] || draft.status}.`,
            });
            await load();
        } catch (_) {}
    };

    return (
        <AdminViewLayout
            pageKey="quotes"
            title="Cotizaciones"
            description="Solicitudes que llegan desde el cotizador del Hero. El backend las guarda y también intenta notificarlas por correo."
            isWide
        >
            <section className="PBADMIN__quotes_metrics">
                <article><span>Total</span><strong>{counters.total}</strong></article>
                <article><span>Nuevas</span><strong>{counters.new}</strong></article>
                <article><span>Presupuestadas</span><strong>{counters.quoted}</strong></article>
                <article><span>Aprobadas</span><strong>{counters.approved}</strong></article>
            </section>

            <section className="PBADMIN__panel PBADMIN__quotes_panel">
                <div className="PBADMIN__quotes_head">
                    <div>
                        <span>LEADS</span>
                        <h2>Solicitudes de presupuesto</h2>
                        <p>Podés seguir cada oportunidad aunque una notificación SMTP llegara a fallar.</p>
                    </div>
                    <button type="button" onClick={load}><FiRefreshCw /> Actualizar</button>
                </div>

                <div className="PBADMIN__quotes_tools">
                    <label><FiSearch /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por código, cliente, proyecto…" /></label>
                    <div className="PBADMIN__quotes_status_filter">
                        <FieldSelectSearch
                            id="quotes_status_filter"
                            label={null}
                            options={STATUS_FILTER_OPTIONS}
                            value={STATUS_FILTER_OPTIONS.find((option) => option.value === status) || STATUS_FILTER_OPTIONS[0]}
                            onChange={(option) => setStatus(option?.value || '')}
                            isClearable={false}
                            isSearchable={false}
                        />
                    </div>
                    <span>{filtered.length} resultado{filtered.length === 1 ? '' : 's'}</span>
                </div>

                <div className="PBADMIN__quotes_list">
                    {visible.map((item) => {
                        const draft = drafts[item.id] || {};
                        const emailSent = item.notification_status === 'sent';
                        return (
                            <article key={item.id} className="PBADMIN__quote_card">
                                <div className="PBADMIN__quote_card_top">
                                    <div>
                                        <span className={`PBADMIN__quote_status is-${item.status}`}>{STATUS_LABELS[item.status] || item.status}</span>
                                        <strong>{item.customer_name}</strong>
                                        <small>{item.quote_code} · {dateLabel(item.created_at)}</small>
                                    </div>
                                    <span className={`PBADMIN__quote_mail ${emailSent ? 'is-sent' : 'is-failed'}`} title={item.notification_error || ''}>
                                        <FiMail /> {emailSent ? 'Email enviado' : 'Email pendiente/fallido'}
                                    </span>
                                </div>

                                <div className="PBADMIN__quote_grid">
                                    <div><span>Proyecto</span><strong>{item.project_type_label || item.project_type}</strong><small>{item.project_stage_label || item.project_stage}</small></div>
                                    <div><span>Plazo</span><strong>{item.timing_label || item.timing}</strong><small>{item.payment_terms || '50% antes · 50% al finalizar'}</small></div>
                                    <div><span>Contacto</span><strong>{item.customer_contact}</strong><small>{item.customer_email || 'WhatsApp / contacto manual'}</small></div>
                                    <div className="is-wide"><span>Detalle</span><p>{item.details || 'Sin detalles adicionales.'}</p></div>
                                </div>

                                {item.notification_error && (
                                    <div className="PBADMIN__quote_notification_error">SMTP: {item.notification_error}</div>
                                )}

                                <div className="PBADMIN__quote_actions">
                                    <div className="PBADMIN__quote_status_editor">
                                        <span>Estado</span>
                                        <FieldSelectSearch
                                            id={`quote_status_${item.id}`}
                                            label={null}
                                            options={STATUS_SELECT_OPTIONS}
                                            value={STATUS_SELECT_OPTIONS.find((option) => option.value === (draft.status || item.status)) || null}
                                            onChange={(option) => updateDraft(item.id, 'status', option?.value || item.status)}
                                            isClearable={false}
                                            isSearchable={false}
                                        />
                                    </div>
                                    <label className="is-note">
                                        <span>Nota interna</span>
                                        <input value={draft.admin_note ?? item.admin_note ?? ''} onChange={(event) => updateDraft(item.id, 'admin_note', event.target.value)} placeholder="Ej. Enviar propuesta mañana, pidió ecommerce…" />
                                    </label>
                                    <button type="button" className="PBADMIN__primary" onClick={() => save(item)} disabled={api.isLoading}><FiSave /> Guardar</button>
                                </div>
                            </article>
                        );
                    })}

                    {visible.length === 0 && (
                        <div className="PBADMIN__quotes_empty">
                            <FiMail />
                            <strong>No hay cotizaciones con estos filtros.</strong>
                        </div>
                    )}
                </div>

                {filtered.length > PER_PAGE && (
                    <div className="PBADMIN__quotes_pagination">
                        <button type="button" disabled={safePage <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>Anterior</button>
                        <span>Página <strong>{safePage}</strong> de {totalPages}</span>
                        <button type="button" disabled={safePage >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>Siguiente</button>
                    </div>
                )}
            </section>
        </AdminViewLayout>
    );
};
